import { SectionHead, Reveal, Crosshair } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { ArrowUpRight, Contact } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Portrait = ({ f, i, t }) => (
  <div className="relative shrink-0 flex flex-col self-start w-full sm:w-auto">
    <Crosshair className="-top-2 -left-2" />
    <Crosshair className="-bottom-2 -right-2" />
    {f.photo ? (
      <div className="chamfer relative overflow-hidden border border-technical w-full sm:w-44 aspect-[4/5]">
        <img
          src={f.photo}
          alt={f.name}
          data-testid={`founder-${i}-photo`}
          style={{ objectPosition: f.photoPos || "50% 20%" }}
          className="absolute inset-0 h-full w-full object-cover grayscale-[35%] contrast-[1.05] transition-all duration-500 group-hover:grayscale-0"
        />
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#16181b]/60 to-transparent" />
      </div>
    ) : (
      <div
        data-testid={`founder-${i}-photo`}
        className="chamfer relative bp-grid border border-dashed border-[rgba(122,22,32,0.4)] bg-canvas w-full sm:w-44 aspect-[4/5] flex flex-col items-center justify-center gap-2"
      >
        <span className="font-display text-5xl font-black text-red-brand/80 leading-none">
          {f.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </span>
        <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] px-3 text-center">
          Portrait — MGT-HQ
        </span>
      </div>
    )}
    {f.vcard && (
      <a
        href={`${API}/api/vcard/${f.vcard}.vcf`}
        download
        data-testid={`founder-${i}-qr`}
        title={t.founders.qrHint}
        className="mt-3 flex w-full sm:w-44 items-center gap-3 border border-technical bg-canvas p-2 transition-colors duration-200 hover:border-red-brand"
      >
        <img src={`${API}/api/vcard/${f.vcard}.qr.svg`} alt={`QR — vCard ${f.name}`} width="56" height="56" loading="lazy" className="h-14 w-14 shrink-0" />
        <span className="font-mono-tech text-[9px] uppercase leading-snug tracking-[0.15em] text-[#525964]">{t.founders.qrHint}</span>
      </a>
    )}
  </div>
);

export const Founders = () => {
  const { t } = useLang();
  return (
    <section id="founders" className="relative bg-sand py-24 sm:py-32 border-b border-technical" data-testid="founders-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={t.founders.eyebrow} title={t.founders.title} testid="founders" />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {t.founders.items.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.12}>
              <article
                data-testid={`founder-card-${i}`}
                className="group relative h-full border border-technical bg-white p-7 sm:p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(22,24,27,0.35)] hover:border-[rgba(122,22,32,0.4)]"
              >
                <div className="flex flex-col sm:flex-row gap-7">
                  <Portrait f={f} i={i} t={t} />
                  <div className="min-w-0">
                    <p className="eyebrow">{f.heritage}</p>
                    <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">{f.name}</h3>
                    <p className="mt-1 font-mono-tech text-[11px] uppercase tracking-[0.18em] text-red-brand">{f.role}</p>
                    {f.badge && (
                      <p data-testid={`founder-${i}-badge`} className="chamfer-sm mt-3 inline-flex items-center gap-2 bg-ink px-3 py-1.5 font-mono-tech text-[10px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4]">
                        <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-[#e63946]" />
                        {f.badge}
                      </p>
                    )}
                    <p className="mt-4 font-mono-tech text-[11px] tracking-wide text-[#24282d] border-l-2 border-red-brand pl-4">
                      {f.experience}
                    </p>
                    {f.stats && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {f.stats.map((s) => (
                          <span key={s} className="bg-sand px-2.5 py-1 font-mono-tech text-[9px] uppercase tracking-[0.12em] text-[#525964]">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="mt-4 text-sm leading-relaxed text-[#525964]">{f.bio}</p>
                    {(f.links.length > 0 || f.email) && (
                      <div className="mt-6 flex flex-wrap gap-2.5">
                        {f.email && (
                          <a
                            data-testid={`founder-${i}-email`}
                            href={`mailto:${f.email}`}
                            className="chamfer-sm inline-flex items-center gap-1.5 border border-red-brand/40 bg-canvas px-3 py-1.5 font-mono-tech text-[10px] font-semibold tracking-[0.12em] text-red-brand transition-colors duration-200 hover:bg-red-brand hover:text-[#f8f7f4]"
                          >
                            {f.email}
                          </a>
                        )}
                        {f.vcard && (
                          <a
                            data-testid={`founder-${i}-vcard`}
                            href={`${API}/api/vcard/${f.vcard}.vcf`}
                            download
                            className="chamfer-sm inline-flex items-center gap-1.5 border border-ink bg-ink px-3 py-1.5 font-mono-tech text-[10px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] transition-colors duration-200 hover:bg-red-brand hover:border-red-brand"
                          >
                            <Contact className="h-3 w-3" /> {t.founders.vcard}
                          </a>
                        )}
                        {f.links.map((l) => (
                          <a
                            key={l.label}
                            data-testid={`founder-${i}-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="chamfer-sm inline-flex items-center gap-1.5 border border-technical bg-canvas px-3 py-1.5 font-mono-tech text-[10px] font-semibold uppercase tracking-[0.15em] text-[#525964] transition-colors duration-200 hover:border-red-brand hover:text-red-brand"
                          >
                            {l.label}
                            <ArrowUpRight className="h-3 w-3" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
